import * as React from 'react'
import styled from 'styled-components'
import * as constants from 'static/constants'
import { agentService } from 'core/services'
import GuideFilter from 'components/organisms/Guides/GuideFilter'
import { buildGuideFilterLabels } from 'utils/weekly'
import { IGuideFilterOptions } from 'static/interfaces'
import SvgIcon from 'components/atoms/SvgIcon'

interface IProps {
  searchGuides: (page: number) => void
  filterOptions: IGuideFilterOptions
  setFilterOptions: any
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  onConfirm?: () => void
}

const GuideSearch: React.VFC<IProps> = React.memo(
  ({ searchGuides, filterOptions, setFilterOptions, page, setPage, onConfirm }) => {
    const [agentSkills, setAgentSkills] = React.useState([])
    const [agentTourSkills, setAgentTourSkills] = React.useState([])
    const [agentRanks, setAgentRanks] = React.useState([])
    const [showFilter, setShowFilter] = React.useState(false)
    const [filterLabels, setFilterLabels] = React.useState([])
    const [loadingFilterParams, setLoadingFilterParams] = React.useState(false)

    const syncSkillsAndRanks = React.useCallback(async () => {
      try {
        setLoadingFilterParams(true)
        const [{ agent_skills }, { agent_tour_skills }, { agent_ranks }] = await Promise.all([
          agentService.getAgentSkills(),
          agentService.getAgentTourSkills(),
          agentService.getAgentRanks(),
        ])

        setAgentSkills(agent_skills)
        setAgentTourSkills(agent_tour_skills)
        // ガイドに紐づけられるのは通常ランクのみのため、フィルタ候補も通常ランクに絞る
        setAgentRanks(agent_ranks.filter(agentRank => agentRank.rank_kind === 'normal'))
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingFilterParams(false)
      }
    }, [agentService.getAgentSkills, agentService.getAgentTourSkills, agentService.getAgentRanks])

    React.useEffect(() => {
      syncSkillsAndRanks()
    }, [])

    React.useEffect(() => {
      setPage(1)
      searchGuides(1)
    }, [filterOptions])

    // ページ更新時に必要であればガイドを取得
    React.useEffect(() => {
      if (page > 1) {
        searchGuides(page)
      }
    }, [page])

    React.useEffect(() => {
      setFilterLabels(
        buildGuideFilterLabels(filterOptions, agentSkills, agentTourSkills, agentRanks)
      )
    }, [filterOptions, agentSkills, agentTourSkills, agentRanks])

    return (
      <Wrapper>
        <div className="filterContainer">
          <div
            className="button filter"
            onClick={() => setShowFilter(true)}
            role="button"
            aria-label="filtering list"
          >
            {filterLabels.length > 0 ? (
              filterLabels.map((option, index) => (
                <div className={`filterLabel ${option.type}`} key={index}>
                  {option.label}
                </div>
              ))
            ) : (
              <>
                <SvgIcon
                  name="filter_list"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                ここから検索
              </>
            )}
          </div>
          {filterLabels.length > 0 && (
            <div
              onClick={() => setFilterOptions(constants.DEFAULT_GUIDE_FILTER_OPTIONS)}
              className="clearFilter"
              role="button"
              aria-label="Clear filtering"
            >
              <SvgIcon name="close" className="icon" ariaHidden={true} role="presentation" />
            </div>
          )}
          {showFilter && (
            <GuideFilter
              filterOptions={filterOptions}
              setFilterOptions={setFilterOptions}
              setShowFilter={setShowFilter}
              allAgentSkills={agentSkills}
              allAgentTourSkills={agentTourSkills}
              allAgentRanks={agentRanks}
              loadingFilterParams={loadingFilterParams}
              onConfirm={onConfirm}
            />
          )}
        </div>
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  .filterContainer {
    position: relative;
    margin: 0 16px 16px;
    pointer-events: auto;
    .clearFilter {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 50%;
      right: 8px;
      width: 24px;
      height: 24px;
      background-color: ${constants.COLORS.primary};
      border-radius: 50%;
      transform: translateY(-50%);
      transition: background-color 0.2s;
      cursor: pointer;
      &:hover {
        background-color: ${constants.COLORS.secondary};
      }
      .icon {
        color: ${constants.COLORS.white};
        width: 16px;
        height: 16px;
      }
    }
    .button.filter {
      display: flex;
      align-items: center;
      font-size: 14px;
      height: 36px;
      line-height: 36px;
      padding: 0 12px;
      transition: opacity 0.2s;
      border: solid 1px ${constants.COLORS.divider};
      border-radius: 4px;
      padding-right: 44px;
      overflow-x: auto;
      overflow-y: hidden;
      cursor: pointer;
      .icon {
        color: ${constants.COLORS.subText};
        margin-right: 8px;
      }
      .filterLabel {
        position: relative;
        padding: 4px 8px;
        margin-right: 8px;
        height: 28px;
        line-height: 20px;
        font-size: 12px;
        border: 1px solid ${constants.COLORS.divider};
        border-radius: 4px;
        white-space: nowrap;
        &:last-child {
          margin-right: 0;
        }
        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: -1px;
          width: 4px;
          height: 16px;
          transform: translateY(-50%);
          border-radius: 0 2px 2px 0;
        }
        &.name {
          &::before {
            background-color: ${constants.COLORS.primary};
          }
        }
        &.ages {
          &::before {
            background-color: #f08a0d;
          }
        }
        &.agent_ranks {
          &::before {
            background-color: ${constants.COLORS.secondary};
          }
        }
      }
    }
  }
`

export default GuideSearch
